import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FilledInput from '@mui/material/FilledInput';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

import { FILTER_CONTROL_ACTIVE_SX, FILTER_CONTROL_DISABLED_SX, FILTER_CONTROL_SX } from '~/theme';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export type ItemType = {
  id: string;
  label: string;
};

type SelectCheckboxProps = {
  label: string;
  options: ItemType[];
  onSelect: (onSelect: string[]) => void;
  value?: string[];
  /**
   * Take one option at a time: radios instead of checkboxes, and the menu
   * closes on pick. `onSelect` still receives an array (empty or one entry), so
   * callers keep a single shape whichever mode they ask for.
   */
  singleSelect?: boolean;
  /** What the closed input reads when the selection narrows nothing. */
  emptyLabel?: string;
  /** Shown, but inert — for pages that can't narrow by this dimension. */
  disabled?: boolean;
};

function SelectCheckbox({
  label,
  options,
  onSelect,
  value,
  singleSelect = false,
  emptyLabel = 'All',
  disabled,
}: SelectCheckboxProps) {
  const selected = value ?? [];

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    if (typeof value === 'string') {
      return;
    }

    onSelect(value);
  };

  // "All" — everything or nothing — is the resting state. Only a narrower
  // selection lights the control up.
  const showsAll = (selectedIds: string[]) =>
    selectedIds.length === options.length || selectedIds.length === 0;

  const labelFor = (id: string) => options.find((option) => option.id === id)?.label ?? id;

  const renderInput = (text: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Box sx={{ color: 'text.secondary', paddingRight: '5px' }}>{label}</Box>
      <Box
        sx={{
          paddingRight: '5px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 500,
          fontSize: '14px',
        }}
      >
        {text}
      </Box>
    </Box>
  );

  const getSelectedLabels = (selectedIds: string[]) => {
    // A disabled control holds nothing, so there is no value to put beside the label.
    // Showing "All" would read as a scope the page had chosen rather than one it can't
    // offer.
    if (disabled) {
      return label;
    }

    return renderInput(showsAll(selectedIds) ? emptyLabel : selectedIds.map(labelFor).join(', '));
  };

  // A single selection *is* the scope, so it is named even when it happens to be
  // the only option — the multi-select rule that collapses a full selection to
  // "All" would otherwise hide which one is in force.
  const getSelectedLabel = (selectedId: string) =>
    renderInput(selectedId ? labelFor(selectedId) : emptyLabel);

  // A disabled control holds nothing, so it never reads as active; a single-select picker does on any selection.
  const isActive = !disabled && (singleSelect ? selected.length > 0 : !showsAll(selected));

  const sharedProps = {
    id: 'select-checkbox',
    displayEmpty: true,
    disabled,
    input: <FilledInput disableUnderline />,
    MenuProps,
  };

  return (
    <div>
      <FormControl
        sx={{
          width: 200,
          '.MuiInputBase-root': {
            ...FILTER_CONTROL_SX,
            ...(isActive && FILTER_CONTROL_ACTIVE_SX),
          },
          // MUI's own disabled input paints a darker `text.disabled` through
          // `-webkit-text-fill-color`, which wins over `color`, so the token's ink has to be
          // repeated through that property too.
          '.MuiInputBase-root.Mui-disabled': {
            ...FILTER_CONTROL_DISABLED_SX,
            '.MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: (theme) => theme.palette.action.disabled,
            },
            // The Period button's caret is the button's own ink at 0.6; without
            // this the dropdown's sits at full strength and reads darker than
            // the label it belongs to.
            '.MuiSelect-icon': { color: 'inherit', opacity: 0.6 },
          },
          '.MuiInputBase-root:before': {
            borderBottom: 'none !important',
          },
          '.MuiInputBase-root:after': {
            borderBottom: 'none !important',
          },
          '.MuiSelect-select': {
            padding: 0,
            // Clears the pill's curve, which would otherwise clip the label.
            paddingLeft: 1.75,
          },
        }}
        size="small"
      >
        {singleSelect ? (
          <Select
            {...sharedProps}
            value={selected[0] ?? ''}
            onChange={(event: SelectChangeEvent) => {
              const next = event.target.value;
              onSelect(next ? [next] : []);
            }}
            renderValue={getSelectedLabel}
          >
            {options.map(({ id, label: optionLabel }) => (
              <MenuItem key={id} value={id}>
                <Radio checked={selected[0] === id} />
                <ListItemText primary={optionLabel} />
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Select
            {...sharedProps}
            multiple
            value={selected}
            onChange={handleChange}
            renderValue={getSelectedLabels}
          >
            {options.map(({ id, label: optionLabel }) => (
              <MenuItem key={id} value={id}>
                <Checkbox checked={selected.some((item) => item === id)} />
                <ListItemText primary={optionLabel} />
              </MenuItem>
            ))}
          </Select>
        )}
      </FormControl>
    </div>
  );
}

export default SelectCheckbox;
